/**
 * The review canvas: tldraw narrowed to architectural redlining, on one
 * plane of a file's annotations. Any surface that reviews (a model plane, a
 * PDF sheet) mounts it over its own background. Its marks are the plane's
 * `records` on the file's Automerge doc (`plane-store.ts`).
 *
 * Restraint is the point: a pen, a highlighter, the revision cloud, the
 * callout with its leader, arrows, boxes, ellipses and text, in red, blue or
 * black, drawn crisp (no hand-drawn wobble) and lettered in the sheet face.
 */
import type { DocHandle } from "@automerge/automerge-repo";
import type { AnnotatedDoc } from "playground/annotations/annotations";
import { useLayoutEffect, useState } from "react";
import { toast } from "sonner";
import {
  createTLStore,
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultFontStyle,
  DefaultSizeStyle,
  DefaultStylePanel,
  DefaultToolbar,
  StylePanelButtonPicker,
  StylePanelSizePicker,
  Tldraw,
  TldrawUiMenuItem,
  useEditor,
  useIsToolSelected,
  useTools,
  useValue,
  type DefaultToolbarProps,
  type Editor,
  type TLDefaultColorStyle,
  type TLUiOverrides,
  type TLUiToolItem,
} from "tldraw";
import { invariant } from "ts-invariant";

import { CloudTool, cloudToolOverride } from "@/review/CloudShapeUtil";
import { syncPlaneStore } from "@/review/plane-store";
import {
  REVIEW_BINDING_UTILS,
  REVIEW_SHAPE_UTILS,
} from "@/review/review-shapes";
import { CalloutTool, calloutToolOverride } from "@/sketch/callout-tool";
import { SKETCH_ASSET_URLS } from "@/sketch/sketch-assets";

import "tldraw/tldraw.css";
import "@/sketch/sketch.css";

/** The review set, in toolbar order: lettering ahead of plain shapes, so a
 *  narrow toolbar overflows the box and ellipse, not the text. */
const REVIEW_TOOLS = [
  "select",
  "hand",
  "draw",
  "highlight",
  "cloud",
  "callout",
  "text",
  "arrow",
  "rectangle",
  "ellipse",
  "eraser",
] as const;

const REVIEW_COLORS: { value: TLDefaultColorStyle; icon: string }[] = [
  { value: "red", icon: "color" },
  { value: "blue", icon: "color" },
  { value: "black", icon: "color" },
];

const TOOLS = [CloudTool, CalloutTool];

/** Every tool outside the review set leaves the toolbar and the keymap. */
const reviewToolsOverride: TLUiOverrides = {
  tools(_editor, tools) {
    return Object.fromEntries(
      REVIEW_TOOLS.map((id) => {
        const tool = tools[id];
        invariant(tool, `No ${id} tool`);
        return [id, tool];
      }),
    );
  },
};
/** Actions that place images or embeds, which a plane can't keep. */
const ASSET_ACTIONS = new Set([
  "flatten-to-image",
  "insert-media",
  "insert-embed",
  "image-replace",
  "video-replace",
]);
const noAssetActionsOverride: TLUiOverrides = {
  actions(_editor, actions) {
    return Object.fromEntries(
      Object.entries(actions).filter(([id]) => !ASSET_ACTIONS.has(id)),
    );
  },
};
const OVERRIDES = [
  cloudToolOverride,
  calloutToolOverride,
  reviewToolsOverride,
  noAssetActionsOverride,
];

function ToolItem({ tool }: { tool: TLUiToolItem }) {
  const selected = useIsToolSelected(tool);
  return <TldrawUiMenuItem {...tool} isSelected={selected} />;
}

function ReviewToolbar(props: DefaultToolbarProps) {
  const tools = useTools();
  return (
    <DefaultToolbar {...props}>
      {REVIEW_TOOLS.map((id) => {
        const tool = tools[id];
        invariant(tool, `No ${id} tool`);
        return <ToolItem key={id} tool={tool} />;
      })}
    </DefaultToolbar>
  );
}

function ReviewStylePanel() {
  const editor = useEditor();
  const color = useValue(
    "review color",
    () => editor.getSharedStyles().get(DefaultColorStyle),
    [editor],
  );
  return (
    <DefaultStylePanel>
      {color !== undefined && (
        <StylePanelButtonPicker
          title="Color"
          uiType="color"
          style={DefaultColorStyle}
          items={REVIEW_COLORS}
          value={color}
        />
      )}
      <StylePanelSizePicker />
    </DefaultStylePanel>
  );
}

/** A plane holds marks, never assets (a plane stores no asset records). */
function refuseAssets() {
  toast.error(
    "A markup plane holds marks only: images and links can't be placed on it.",
  );
}

/** New marks are redlines: red, solid, unfilled, lettered in the sans face,
 *  and signed by who drew them (a callout's note is its thread's first
 *  message, so it needs an author). What would bring assets onto the plane
 *  (dropped files, pasted images, links, embeds) is refused out loud. */
function startReviewing(editor: Editor, author: MarkAuthor) {
  // Drawn here, not loaded: a mark from the doc keeps who drew it.
  editor.sideEffects.registerBeforeCreateHandler("shape", (shape, source) =>
    source === "user"
      ? {
          ...shape,
          meta: {
            ...shape.meta,
            author: author.id,
            authorName: author.name,
            createdAt: Date.now(),
          },
        }
      : shape,
  );
  editor.setStyleForNextShapes(DefaultColorStyle, "red");
  editor.setStyleForNextShapes(DefaultDashStyle, "solid");
  editor.setStyleForNextShapes(DefaultFillStyle, "none");
  editor.setStyleForNextShapes(DefaultFontStyle, "sans");
  editor.setStyleForNextShapes(DefaultSizeStyle, "s");
  for (const type of [
    "files",
    "url",
    "svg-text",
    "embed",
    "excalidraw",
  ] as const) {
    editor.registerExternalContentHandler(type, refuseAssets);
  }
  editor.registerExternalContentHandler("tldraw", ({ content, point }) => {
    if (content.assets.length) return refuseAssets();
    editor.putContentOntoCurrentPage(content, { point, select: true });
  });
}

/** Who is drawing: each mark records it. */
export interface MarkAuthor {
  id: string;
  name: string;
}

export interface ReviewCanvasProps {
  handle: DocHandle<AnnotatedDoc>;
  planeId: string;
  author: MarkAuthor;
  /** What the surface does with the editor; may return its cleanup. */
  onMount?: (editor: Editor) => void | (() => void);
}

/** One store and editor per plane: its undo history is that plane's alone. */
export function ReviewCanvas(props: ReviewCanvasProps) {
  return (
    <PlaneCanvas key={`${props.handle.url}:${props.planeId}`} {...props} />
  );
}

function PlaneCanvas({ handle, planeId, author, onMount }: ReviewCanvasProps) {
  const [store] = useState(() =>
    createTLStore({
      shapeUtils: REVIEW_SHAPE_UTILS,
      bindingUtils: REVIEW_BINDING_UTILS,
    }),
  );
  const [synced, setSynced] = useState(false);
  // The plane's page has to exist before the editor mounts on the store.
  useLayoutEffect(() => {
    const stop = syncPlaneStore(store, handle, planeId);
    setSynced(true);
    return stop;
  }, [store, handle, planeId]);

  return synced ? (
    <div data-sketch data-review className="h-full w-full">
      <Tldraw
        licenseKey={import.meta.env.VITE_TLDRAW_LICENSE_KEY}
        store={store}
        assetUrls={SKETCH_ASSET_URLS}
        options={{ maxPages: 1 }}
        shapeUtils={REVIEW_SHAPE_UTILS}
        bindingUtils={REVIEW_BINDING_UTILS}
        tools={TOOLS}
        overrides={OVERRIDES}
        components={{
          Toolbar: ReviewToolbar,
          StylePanel: ReviewStylePanel,
          MainMenu: null,
          PageMenu: null,
          NavigationPanel: null,
          HelpMenu: null,
          ActionsMenu: null,
          QuickActions: null,
          ZoomMenu: null,
          Minimap: null,
          DebugMenu: null,
          DebugPanel: null,
          SharePanel: null,
          MenuPanel: null,
        }}
        onMount={(editor) => {
          startReviewing(editor, author);
          return onMount?.(editor);
        }}
      />
    </div>
  ) : null;
}
